import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useDeepCompareCallback, useDeepCompareEffect } from 'use-deep-compare';
import _get from 'lodash/get';

import { constants, formatMailtoContent, locale } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useUpdateTrqkGapChannelAudiosMutation } from '@trqk/api';
import { Button, IconWarningSign, IconUpdate } from '@trqk/components';

import { StepWrapper } from '../..';
import { ConfirmWorksLoadingState, ConfirmWorksTable } from './components';
import {
    useToast,
    useLogData,
    useGapAnalysisTools,
} from '../../../../../../hooks';

import { getInitialState, sortConfirmWorksData } from './utils';

const { GET_ACTIVE_LOG_POLLS, GET_SESSION_CURRENT_USER } = queryStrings;

const CONFIRM_WORKS_HANDLER_KEY = 'confirmWorks';

// The second input variable is called `gap_channel_id`, but it actually refers to a
// `channel.channel_id`
const ANALYZE_TRQK_GAP_CHANNEL = gql`
    mutation analyzeTrqkGapChannel(
        $gap_analysis_id: String!
        $gap_channel_id: String!
    ) {
        analyzeTrqkGapChannel(
            gap_analysis_id: $gap_analysis_id
            gap_channel_id: $gap_channel_id
        ) {
            channel_id
        }
    }
`;

const emailContent =
    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.CONFIRM_WORKS.ERROR_STATE
        .EMAIL_CONTENT;

/**
 * Renders a table of the works for a specific channel. Allows the user to match each detection work
 * to a statement work and select the matched pair for inclusion in a Gap Analysis report.
 * @param props - {@link GapAnalysisStepProps}
 */
export const ConfirmWorks: GapAnalysisStepComponent = props => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const { add: addToast } = useToast();
    const client = useApolloClient();
    const { addHandler, deleteHandlers } = useLogData();
    const { fetchAndUpdateAudioLists, fetchAndSetChannels } =
        useGapAnalysisTools();
    const { context, remove, send } = props;
    const { channels, audioLists } = context;
    const currentChannelId = context?.currentChannelId ?? null;
    const audioList = currentChannelId
        ? audioLists?.[currentChannelId] ?? []
        : [];
    const audioEdits = currentChannelId
        ? context.edits?.[currentChannelId]?.audioEdits ?? null
        : null;

    const channel =
        currentChannelId === null
            ? null
            : channels.find(
                  ({ channel_id }) => channel_id === currentChannelId,
              ) ?? null;
    const channelStatus = channel?.channel_status ?? null;
    const gapAnalysisId = context?.trqkGap?.gap_analysis_id ?? null;
    const [refreshTime, setRefreshTime] = useState(0);

    const [data, setData] = useState<Maybe<TrqkGapChannelAudio[]>>(null);
    const [initialSelected, setInitialSelected] =
        useState<Maybe<Record<string, boolean>>>(null);
    const [initialExpanded, setInitialExpanded] =
        useState<Maybe<Record<string, boolean>>>(null);

    useDeepCompareEffect(() => {
        if (refreshTime > 0) {
            setData(sortConfirmWorksData(audioList));

            const { selected, expanded } = getInitialState(audioList);

            setInitialSelected(selected);
            setInitialExpanded(expanded);
        }
    }, [audioList, refreshTime]);

    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (hasError) {
            send({
                type: 'UPDATE_STEP',
                value: {
                    stepId: 'confirmWorks',
                    update: { disablePreviousStep: false },
                },
            });
        }
    }, [hasError]);

    const showTable =
        !hasError &&
        data !== null &&
        initialSelected !== null &&
        initialExpanded !== null;

    const [showLoadingState, setShowLoadingState] = useState(
        channelStatus === 'RAW' && !showTable,
    );

    const [analyzeTrqkGapChannel] = useMutation<{
        analyzeTrqkGapChannel: TrqkGapChannel;
    }>(ANALYZE_TRQK_GAP_CHANNEL, {
        onError: () => {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.API_ERRORS.analyzeTrqkGapChannel,
                logMessage: 'analyzeTrqkGapChannel',
            });

            setHasError(true);
        },
    });

    /**
     * Use fetchAndUpdateAudioLists(), a function provided by the GapAnalysisToolsProvider, which
     * uses listGapChannelAudios to query for the list of audios, then updates the audio list for
     * this channelId.
     */
    const fetchAudiosAndManageState = useCallback(() => {
        if (gapAnalysisId && currentChannelId) {
            fetchAndUpdateAudioLists(gapAnalysisId, currentChannelId)
                .then(nextAudios => {
                    client.cache.writeQuery({
                        query: GET_ACTIVE_LOG_POLLS,
                        data: {
                            uiState: {
                                __typename: 'UIState',
                                activeLogPolls: {
                                    __typename: 'ActiveLogPolls',
                                    analyzeTrqkGapChannel: false,
                                },
                            },
                        },
                    });

                    send({
                        type: 'UPDATE_STEP',
                        value: {
                            stepId: 'confirmWorks',
                            update: {
                                disablePreviousStep: false,
                            },
                        },
                    });

                    if (nextAudios) {
                        setRefreshTime(Date.now());

                        setTimeout(() => {
                            send({
                                type: 'SET_LOADING',
                                value: {
                                    loading: false,
                                },
                            });
                        }, 1000);
                    } else {
                        setHasError(true);

                        addToast({
                            kind: 'warning-banner',
                            content:
                                locale.enUS.API_ERRORS.listGapChannelAudios,
                            logMessage: 'listGapChannelAudios',
                        });

                        send({
                            type: 'SET_LOADING',
                            value: {
                                loading: false,
                            },
                        });
                    }
                })
                .catch(() => {
                    setHasError(true);

                    client.cache.writeQuery({
                        query: GET_ACTIVE_LOG_POLLS,
                        data: {
                            uiState: {
                                __typename: 'UIState',
                                activeLogPolls: {
                                    __typename: 'ActiveLogPolls',
                                    analyzeTrqkGapChannel: false,
                                },
                            },
                        },
                    });

                    send({
                        type: 'SET_LOADING',
                        value: {
                            loading: false,
                        },
                    });

                    addToast({
                        kind: 'warning-banner',
                        content: locale.enUS.API_ERRORS.listGapChannelAudios,
                        logMessage: 'listGapChannelAudios',
                    });
                });
        }
    }, [fetchAndUpdateAudioLists, currentChannelId, gapAnalysisId]);

    useEffect(() => {
        if (currentChannelId && gapAnalysisId) {
            if (channelStatus === 'RAW') {
                client.cache.writeQuery({
                    query: GET_ACTIVE_LOG_POLLS,
                    data: {
                        uiState: {
                            __typename: 'UIState',
                            activeLogPolls: {
                                __typename: 'ActiveLogPolls',
                                analyzeTrqkGapChannel: true,
                            },
                        },
                    },
                });

                analyzeTrqkGapChannel({
                    variables: {
                        gap_analysis_id: gapAnalysisId,
                        gap_channel_id: currentChannelId,
                    },
                });
            } else if (
                channelStatus === 'ANALYZED' ||
                channelStatus === 'MATCHED'
            ) {
                fetchAudiosAndManageState();
                setHasError(false);
            } else if (channelStatus === 'ERROR') {
                setHasError(true);

                send({
                    type: 'SET_LOADING',
                    value: {
                        loading: false,
                    },
                });
            }
        }
    }, [currentChannelId, gapAnalysisId, channelStatus]);

    /**
     * Handles the `analyzeTrqkGapChannel` event by fetching the audios for this channel and
     * updating various pieces of state.
     */
    const handleAnalyzeGapChannel = useCallback(
        (event: LogEvent) => {
            const analyzedGapChannelId = event.detail?.channel_id ?? null;

            if (
                event.eventStatus === 'success' &&
                currentChannelId &&
                currentChannelId === analyzedGapChannelId &&
                gapAnalysisId
            ) {
                setShowLoadingState(false);

                fetchAudiosAndManageState();

                send({
                    type: 'UPDATE_CHANNEL_STATUS',
                    value: {
                        channelId: analyzedGapChannelId,
                        status: 'ANALYZED' as TrqkGapChannelStatus,
                    },
                });
            }

            if (event.eventStatus === 'failure') {
                setShowLoadingState(false);

                send({
                    type: 'SET_LOADING',
                    value: {
                        loading: false,
                    },
                });

                if (analyzedGapChannelId) {
                    send({
                        type: 'UPDATE_CHANNEL_STATUS',
                        value: {
                            channelId: analyzedGapChannelId,
                            status: 'ERROR' as TrqkGapChannelStatus,
                        },
                    });
                }

                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.LOG_EVENT_ERRORS.analyzeGapChannel,
                    logMessage: 'analyzeGapChannel',
                });

                send({
                    type: 'UPDATE_STEP',
                    value: {
                        stepId: 'confirmWorks',
                        update: {
                            disablePreviousStep: false,
                        },
                    },
                });
            }
        },
        [currentChannelId, gapAnalysisId],
    );

    useEffect(() => {
        addHandler({
            eventTypes: ['analyzeGapChannel'],
            handler: handleAnalyzeGapChannel,
            handlerKey: CONFIRM_WORKS_HANDLER_KEY,
        });

        return () => {
            deleteHandlers(CONFIRM_WORKS_HANDLER_KEY);
        };
    }, [handleAnalyzeGapChannel]);

    const [updateTrqkGapChannelAudios] = useUpdateTrqkGapChannelAudiosMutation({
        fetchPolicy: 'no-cache',
    });

    /**
     * Add an audio edit to update the matched `songId` when the user sets a new value in the
     * StatementWorkCell dropdown.
     */
    const updateAudioMatch: UpdateAudioMatch = useDeepCompareCallback(
        async (gapAudioId: string, songId: Maybe<string>): Promise<boolean> => {
            if (currentChannelId) {
                send({
                    type: 'EDIT_AUDIO_MATCHED_WORK_ID',
                    value: {
                        gapAudioId,
                        channelId: currentChannelId,
                        songId,
                    },
                });

                return true;
            }

            return false;
        },
        [currentChannelId, send],
    );

    /**
     * Update all audios that have been edited in this channel by calling the
     * updateTrqkGapChannelAudios mutation. This function is called when the user clicks "Apply".
     */
    const applyPendingAudioEdits = useDeepCompareCallback(async () => {
        if (!currentChannelId || !gapAnalysisId || audioEdits === null) {
            return null;
        }

        const gapAudioUpdates = Object.values(audioEdits).reduce(
            (
                acc: TrqkGapChannelAudioUpdate[],
                GapAudioEdit: GapAudioEdit,
            ): TrqkGapChannelAudioUpdate[] => {
                const { gapAudioId, edited, original } = GapAudioEdit;

                acc.push({
                    channel_id: currentChannelId,
                    gap_audio_id: gapAudioId,
                    include: _get(edited, 'include', original.include),
                    matched_work_id: _get(edited, 'songId', original.songId),
                });

                return acc;
            },
            [],
        );

        if (gapAudioUpdates.length === 0) {
            // If there are no updates to make, we do not call the mutation, but we still clear the
            // audioEdits list in case it contains changes that toggled values back and forth.
            send({
                type: 'CLEAR_AUDIO_EDITS',
                value: {
                    channelId: currentChannelId,
                },
            });

            return true;
        }

        const result = await updateTrqkGapChannelAudios({
            variables: {
                gap_analysis_id: gapAnalysisId,
                gap_channel_id: currentChannelId,
                gap_audios: gapAudioUpdates,
            },
        });

        if (result.data) {
            // This causes the table to stop rendering, so that when the refreshTime is set to a
            // new value and picked up by the useDeepCompareEffect above, the table will be
            // instantiated with new initial state. This prevents a bug where the row grouping is
            // not re-initialized after saving edits.
            setInitialSelected(null);
            setInitialExpanded(null);

            // Fetch updated gapAudios with listGapChannelAudios and update audio lists in
            // state machine.
            fetchAudiosAndManageState();

            // Clear out the audio edits because we just saved them with updateTrqkGapChannelAudios
            send({
                type: 'CLEAR_AUDIO_EDITS',
                value: {
                    channelId: currentChannelId,
                },
            });

            return true;
        }

        addToast({
            kind: 'warning-banner',
            content: locale.enUS.API_ERRORS.updateTrqkGapChannelAudios,
            logMessage: 'updateTrqkGapChannelAudios',
        });

        return null;
    }, [audioEdits, audioList, currentChannelId, gapAnalysisId]);

    const beforePreviousStep = useCallback(async () => {
        if (currentChannelId) {
            send({
                type: 'CLEAR_AUDIO_EDITS',
                value: {
                    channelId: currentChannelId,
                },
            });
        }

        if (gapAnalysisId) {
            const nextChannels = await fetchAndSetChannels(gapAnalysisId);

            if (nextChannels === null) {
                return false;
            }
        }

        return true;
    }, [currentChannelId]);

    /**
     * This function applies edits made by the user, but doesn't actually move them to the next
     * step. This is intentional - see notes in:
     * packages/web/src/hooks/useGapAnalysisTools/constants.ts line 124
     */
    const applyEdits = useCallback(async () => {
        if (currentChannelId && gapAnalysisId) {
            const applyEditsResult = await applyPendingAudioEdits();

            if (applyEditsResult === null) {
                return false;
            }

            return true;
        }

        return false;
    }, [
        currentChannelId,
        gapAnalysisId,
        applyPendingAudioEdits,
        fetchAndSetChannels,
    ]);

    const mailtoContent = useMemo(
        () =>
            formatMailtoContent({
                email: constants.SUPPORT_EMAIL_ADDRESS,
                subject: emailContent.SUBJECT,
                bodyLines: [
                    emailContent.GREETING,
                    `${emailContent.BODY_PART_1} ${userId}`,
                    `${emailContent.BODY_PART_2} ${gapAnalysisId}`,
                    `${emailContent.BODY_PART_3} ${currentChannelId}`,
                    emailContent.BODY_PART_4,
                    emailContent.SIGN_OFF,
                ],
            }),
        [userId, gapAnalysisId, currentChannelId],
    );

    return (
        <StepWrapper
            context={context}
            loadingState={
                showLoadingState ? (
                    <ConfirmWorksLoadingState
                        channelName={channel?.channel_name_detection ?? null}
                    />
                ) : null
            }
            remove={remove}
            resetPreviousStepTitle={true}
            send={send}
            helpLinkHref="https://www.trqk.io/faq#gap-analysis-works"
            beforePreviousStep={beforePreviousStep}
            beforeNextStep={applyEdits}
        >
            {hasError && (
                <div className="flex flex-col items-center justify-center h-full">
                    <div
                        className="grid items-center"
                        style={{
                            gridTemplateColumns: '30px auto',
                        }}
                    >
                        <IconWarningSign fillColor="tertiary-400" width={24} />
                        <p className="mt-1 italic text-tertiary-400">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .CONFIRM_WORKS.ERRORS.CHANNEL_STATUS_ERROR
                            }
                        </p>
                    </div>
                    {channelStatus !== 'ANALYZED' && (
                        <Button
                            addClasses="mt-6"
                            autoWidth={true}
                            color="secondary-light-bg"
                            handleClick={() => {
                                send({
                                    type: 'SET_LOADING',
                                    value: {
                                        loading: true,
                                    },
                                });

                                setShowLoadingState(true);

                                analyzeTrqkGapChannel({
                                    variables: {
                                        gap_analysis_id: gapAnalysisId,
                                        gap_channel_id: currentChannelId,
                                    },
                                });
                            }}
                        >
                            <p className="px-6">
                                <IconUpdate
                                    addClasses="mr-1"
                                    fillColor="white"
                                    width={20}
                                />
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .CONFIRM_WORKS.ERROR_STATE.RETRY
                                }
                            </p>
                        </Button>
                    )}
                    <a
                        className="mt-6 text-sm underline text-primary-500"
                        href={mailtoContent}
                    >
                        {
                            locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.CONFIRM_WORKS
                                .ERROR_STATE.CONTACT_SUPPORT
                        }
                    </a>
                </div>
            )}

            {/*
                Use `showTable` to avoid rendering the table before we've finished loading the
                audioList and setting up initialState.
            */}
            {showTable && (
                <ConfirmWorksTable
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- we've already prevented a type mismatch here with showTable
                    // @ts-ignore
                    data={data}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- we've already prevented a type mismatch here with showTable
                    // @ts-ignore
                    initialExpanded={initialExpanded}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- we've already prevented a type mismatch here with showTable
                    // @ts-ignore
                    initialSelected={initialSelected}
                    updateAudioMatch={updateAudioMatch}
                />
            )}
        </StepWrapper>
    );
};
